import { Box, Stack } from '@mui/material';
import type { GetContractsResponse } from '@vy/api-definition';
import type { ContractAdditionalFilter, ContractStatus } from '@vy/types';
import { CONTRACT_ADDITIONAL_FILTERS, CONTRACT_STATUSES } from '@vy/types';
import { values } from '@vy/utils';
import React, { useCallback } from 'react';
import { useAdditionalFiltersParam } from './hooks/use-additional-filters-param';
import { useContractStatusesParam } from './hooks/use-contract-statuses-param';
import { SummaryCard } from '../../../../components/new-design/data-display/SummaryCard/SummaryCard';
import { Active, Alarm, Inactive, PendingContractArrowCircle, UpcomingRenewal } from '@vy/ui-design-system';

export type PlansHeaderSummarizeProps = {
  data?: Omit<GetContractsResponse, 'contracts'>;
};

type SummaryDataType = {
  title: string;
  value: number | undefined;
  filterKey: ContractStatus | ContractAdditionalFilter;
  icon: React.ReactNode;
  iconBackground: string;
}[];

export const PlansHeaderSummarize: React.FC<PlansHeaderSummarizeProps> = ({ data }) => {
  const [selectedContractStatuses, setSelectedContractStatuses] = useContractStatusesParam();
  const [selectedAdditionalFilters, setSelectedAdditionalFilters] = useAdditionalFiltersParam();
  const summaryData = getSummaryData(data);

  const onFilterSelect = useCallback(
    (filterKey: ContractStatus | ContractAdditionalFilter) => {
      if (isContractStatus(filterKey)) {
        setSelectedAdditionalFilters([]);
        toggleFilter(selectedContractStatuses ?? [], setSelectedContractStatuses, filterKey);
      }

      if (isAdditionalFilter(filterKey)) {
        setSelectedContractStatuses([]);
        toggleFilter(selectedAdditionalFilters ?? [], setSelectedAdditionalFilters, filterKey);
      }
    },
    [
      selectedContractStatuses,
      setSelectedContractStatuses,
      selectedAdditionalFilters,
      setSelectedAdditionalFilters,
    ],
  );

  return (
    <Stack
      direction="row"
      sx={{
        gap: 1,
        alignItems: "center",
        width: "100%",
        flexWrap: "wrap"
      }}>
      {summaryData.map(({ title, value, filterKey, icon, iconBackground }) => {
        const filterSource = isContractStatus(filterKey)
          ? selectedContractStatuses
          : selectedAdditionalFilters;
        const isActive = filterSource?.includes(filterKey as never);

        return (
          <Box
            key={filterKey}
            sx={{
              flex: "1 1 0",
              minWidth: "220px"
            }}>
            <SummaryCard
              title={title}
              value={value}
              onClick={() => onFilterSelect(filterKey)}
              icon={icon}
              iconBackground={iconBackground}
              isActive={isActive ?? false}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

function isContractStatus(key: ContractStatus | ContractAdditionalFilter): key is ContractStatus {
  return values<string>(CONTRACT_STATUSES).includes(key);
}

function isAdditionalFilter(key: ContractStatus | ContractAdditionalFilter): key is ContractAdditionalFilter {
  return values<string>(CONTRACT_ADDITIONAL_FILTERS).includes(key);
}

function getSummaryData(data: PlansHeaderSummarizeProps['data']): SummaryDataType {
  return [
    {
      title: 'Active Contracts',
      value: data?.countedActiveContracts,
      filterKey: CONTRACT_STATUSES.Active,
      icon: <Active />,
      iconBackground: '#DFFAE5',
    },
    {
      title: 'Pending Review',
      value: data?.countedInReviewContracts,
      filterKey: CONTRACT_STATUSES.InReview,
      icon: <PendingContractArrowCircle stroke="#7E4CFF" />,
      iconBackground: '#F4F2FF',
    },
    {
      title: `Upcoming Renewals`,
      value: data?.countedUpcomingRenewalContracts,
      filterKey: CONTRACT_ADDITIONAL_FILTERS.UPCOMING_RENEWALS,
      icon: <UpcomingRenewal />,
      iconBackground: '#FDF1D7',
    },
    {
      title: 'Inactive Contracts',
      value: data?.countedInactiveContracts,
      filterKey: CONTRACT_STATUSES.Inactive,
      icon: <Inactive />,
      iconBackground: '#E8EBF2',
    },
    {
      title: 'Expired Contracts',
      value: data?.countedExpiredContracts,
      filterKey: CONTRACT_STATUSES.Expired,
      icon: <Alarm height={16} width={16} />,
      iconBackground: '#FEF3F4',
    },
  ];
}

function toggleFilter<Filter extends ContractStatus | ContractAdditionalFilter>(
  currentFilters: Filter[],
  setFilters: (filters: Filter[]) => void,
  filterKey: Filter,
) {
  const isActive = currentFilters.includes(filterKey);
  const filter = isActive ? [] : [filterKey];

  setFilters(filter);
}
